import type { MessageKey } from '@mendikot/i18n';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router';
import { useAuthUser, useRemoteSettings, useServices, useSettings, useT } from '../app/context';
import { useTheme } from '../app/theme';
import { EmblemShape } from '../components/cards/Emblem';
import { PlayingCard } from '../components/cards/PlayingCard';
import { Icon, type IconName } from '../components/ui/Icon';
import { Avatar, IconButton } from '../components/ui/primitives';
import { useReduceMotion } from '../hooks/useMotionPrefs';
import { InstallButton, PushPrompt } from './prompts';
import { loadPractice } from './practiceStore';

function ActionCard({
  icon,
  title,
  desc,
  onClick,
  primary,
  delay,
}: {
  icon: IconName;
  title: string;
  desc?: string;
  onClick(): void;
  primary?: boolean;
  delay: number;
}) {
  const reduce = useReduceMotion();
  return (
    <motion.button
      onClick={onClick}
      className={
        primary
          ? 'press hover-lift group flex w-full items-center gap-4 rounded-[26px] bg-accent p-4 text-left text-accent-ink shadow-[0_18px_40px_-20px_var(--c-accent)]'
          : 'press hover-lift panel group flex w-full items-center gap-4 rounded-[26px] p-4 text-left'
      }
      initial={reduce ? { opacity: 0 } : { opacity: 0, transform: 'translateY(10px)' }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, transform: 'translateY(0px)' }}
      transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1], delay }}
    >
      <span
        className={
          primary
            ? 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-black/10'
            : 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent-soft text-accent'
        }
      >
        <Icon name={icon} size={22} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-lg font-extrabold leading-tight">{title}</span>
        {desc && (
          <span className={primary ? 'block text-sm opacity-75' : 'block text-sm text-ink-muted'}>
            {desc}
          </span>
        )}
      </span>
      <Icon name="chevronRight" size={18} className="opacity-60" />
    </motion.button>
  );
}

export function Home() {
  const t = useT();
  const navigate = useNavigate();
  const user = useAuthUser();
  const theme = useTheme();
  const remote = useRemoteSettings();
  const name = useSettings((s) => s.name);
  const avatarId = useSettings((s) => s.avatarId);
  const { online } = useServices();
  const reduce = useReduceMotion();
  const savedPractice = loadPractice();
  const lang = useSettings((s) => s.lang);
  // Remote Config can override the greeting per language from the console.
  const greeting = remote.themeOverrides.banner?.[lang] ?? remote.themeOverrides.banner?.en;

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-xl flex-col px-4 pb-[calc(20px+var(--safe-bottom))] pt-[calc(8px+var(--safe-top))]">
      <header className="flex items-center justify-between">
        <button
          className="press flex items-center gap-2 rounded-full py-1 pl-1 pr-3 hover:bg-white/5"
          onClick={() => navigate('/profile')}
          aria-label={t('profile.title')}
        >
          <Avatar name={name || '?'} avatarId={avatarId} size={36} />
          <span className="text-sm font-bold text-ink">{name || t('profile.title')}</span>
          {user?.isAnonymous && online && (
            <span className="h-2 w-2 rounded-full bg-warning" aria-hidden="true" />
          )}
        </button>
        <div className="flex items-center gap-1">
          <InstallButton />
          <IconButton
            icon="settings"
            label={t('settings.title')}
            onClick={() => navigate('/settings')}
          />
        </div>
      </header>

      <section className="relative mt-6 flex flex-col items-center text-center">
        <div className="relative h-36 w-64" aria-hidden="true">
          {[-16, 0, 16].map((rot, i) => (
            <motion.div
              key={rot}
              className="absolute left-1/2 top-2 w-[78px]"
              style={{ marginLeft: -39, zIndex: i }}
              initial={reduce ? false : { rotate: 0, y: 30, opacity: 0 }}
              animate={{ rotate: rot, x: rot * 3.2, y: Math.abs(rot) * 0.5, opacity: 1 }}
              transition={{ type: 'spring', duration: 0.7, bounce: 0.2, delay: 0.08 * i }}
            >
              <PlayingCard cardId={i === 1 ? '0:H:10' : i === 0 ? '0:S:10' : '0:D:10'} />
            </motion.div>
          ))}
          <div className="absolute -bottom-2 left-1/2 h-14 w-14 -translate-x-1/2 text-accent opacity-80">
            <svg viewBox="0 0 100 100" className="h-full w-full">
              <EmblemShape emblem={theme.emblem} />
            </svg>
          </div>
        </div>
        <h1 className="brass-text mt-4 font-display text-5xl leading-none">{t('app.name')}</h1>
        <p className="mt-2 text-ink-muted">
          {remote.themeBannerEnabled || theme.id !== 'default'
            ? (greeting ?? t(theme.greetingKey as MessageKey))
            : t('app.tagline')}
        </p>
        {name && (
          <p className="mt-1 text-sm font-semibold text-ink-faint">
            {t('home.greeting', { name })}
          </p>
        )}
      </section>

      {remote.announcement.title && (
        <a
          href={remote.announcement.link ?? '#'}
          className="panel mt-5 block rounded-2xl p-3 text-sm"
          target={remote.announcement.link?.startsWith('http') ? '_blank' : undefined}
          rel="noreferrer"
        >
          <div className="font-bold text-ink">{remote.announcement.title}</div>
          {remote.announcement.body && (
            <div className="text-ink-muted">{remote.announcement.body}</div>
          )}
        </a>
      )}

      <nav className="mt-8 space-y-3" aria-label={t('app.name')}>
        {savedPractice && (
          <ActionCard
            icon="play"
            title={t('home.continueGame')}
            desc={t('home.practice')}
            onClick={() => navigate('/practice')}
            primary
            delay={0}
          />
        )}
        <ActionCard
          icon="users"
          title={t('home.playFriends')}
          desc={online ? t('home.playFriendsDesc') : t('home.onlineUnavailable')}
          onClick={() => navigate(online ? '/play/new' : '/practice/new')}
          primary={!savedPractice}
          delay={0.05}
        />
        <ActionCard
          icon="key"
          title={t('home.join')}
          onClick={() => navigate('/join')}
          delay={0.1}
        />
        <ActionCard
          icon="bot"
          title={t('home.practice')}
          desc={t('home.practiceDesc')}
          onClick={() => navigate('/practice/new')}
          delay={0.15}
        />
        <ActionCard
          icon="book"
          title={t('home.howTo')}
          onClick={() => navigate('/how-to-play')}
          delay={0.2}
        />
      </nav>

      <div className="mt-6">
        <PushPrompt />
      </div>

      <footer className="mt-8 text-center text-xs text-ink-faint">
        <button className="underline-offset-2 hover:underline" onClick={() => navigate('/privacy')}>
          {t('settings.privacyPolicy')}
        </button>
      </footer>
    </div>
  );
}
